import { useState, useContext, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import CircularProgress from "@mui/material/CircularProgress";

import Form from "../../../Form/Form";
import Input from "../../../Form/Input/Input";
import DragDropFileUpload from "../../../DragDropFileUpload/DragDropFileUpload";
import DefaultButton from "../../../simple/DefaultButton";
import StackedProgress from "../../../StackedProgress/StackedProgress";
import Modal from "../../../Modal/Modal";

import { useAuth } from "../../../../contexts/AuthContext";
import { BrandContext } from "../../../../App";
import { useAPI } from "/src/hooks/useAPI";

import PeopleOutlineIcon from "@mui/icons-material/PeopleOutline";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";

const TitleWatcher = ({ watch, setTranscriptData }) => {
  const watchedTitle = watch("conversation_insight_name");
  useEffect(() => {
    if (watchedTitle) {
      setTranscriptData((prev) => ({ ...prev, name: watchedTitle }));
    }
  }, [watchedTitle, setTranscriptData]);
  return null;
};

const ProgramWatcher = ({
  setValue,
  options,
  setTranscriptData,
  currentProgramId,
}) => {
  useEffect(() => {
    if (options && options.length > 0 && !currentProgramId) {
      const firstOptionValue = options[0].value;
      setValue("program_filter", firstOptionValue);
      setTranscriptData((prev) => ({
        ...prev,
        training_program_id: firstOptionValue,
      }));
    }
  }, [options, currentProgramId, setValue, setTranscriptData]);
  return null;
};

const CIWidget = () => {
  const { authToken } = useAuth();
  const { brand, allTrainingPrograms } = useContext(BrandContext);
  const { callAPI } = useAPI();
  const navigate = useNavigate();

  const [isLoading, setIsLoading] = useState(false);
  const [generating, setIsGenerating] = useState(false);
  const [isValidFile, setIsValidFile] = useState(false);
  const [modal, setModal] = useState({ type: "", show: false, title: "" });
  const [transcriptData, setTranscriptData] = useState({
    file: "",
    name: "",
    subject: "",
    training_program_id: "",
    availableSubjects: [],
    generationProgress: {},
    id: "",
  });

  const trainingProgramOptions = useMemo(() => {
    if (!allTrainingPrograms) return [];
    return Object.values(allTrainingPrograms).map((p: any) => ({
      label: p.name,
      value: p.id,
    }));
  }, [allTrainingPrograms]);

  const handleFilesSelected = (
    files,
    setUploadProgress,
    setError,
    setSuccess,
  ) => {
    setIsLoading(true);
    files.forEach((file, index) => {
      const formData = new FormData();
      formData.append("transcript", file);

      setTranscriptData((prev) => ({ ...prev, name: file.name, file }));

      const xhr = new XMLHttpRequest();

      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable) {
          const progress = Math.round((event.loaded / event.total) * 100);
          setUploadProgress((prev) =>
            prev.map((item, i) => (i === index ? { ...item, progress } : item)),
          );
        }
      };

      xhr.onload = () => {
        try {
          const response = JSON.parse(xhr.response);
          if (xhr.status >= 200 && xhr.status < 300) {
            if (response.valid === false) {
              setSuccess(false);
              setIsValidFile(false);
              setError(response.error?.transcript?.[0] || "Invalid file.");
            } else {
              setSuccess("Transcript validated successfully");
              setIsValidFile(true);
              setError(null);
              setTranscriptData((prev) => ({
                ...prev,
                availableSubjects: response.preview.speakers,
              }));
            }
          } else {
            setSuccess(false);
            setError(
              response.error?.transcript
                ? response.error.transcript[0]
                : response.error || `Upload failed: ${xhr.statusText}`,
            );
          }
        } catch {
          setSuccess(false);
          setError(`Server error: ${xhr.status}`);
        }
        setIsLoading(false);
      };

      xhr.open(
        "POST",
        `${brand.api_base_url}/conversation_insight/validate`,
        true,
      );
      xhr.setRequestHeader("Authorization", `Token ${authToken}`);
      xhr.send(formData);
    });
  };

  const pollFeedbackStatus = async (insightId) => {
    try {
      const response = await callAPI(
        `conversation_insight/status/${insightId}`,
        {
          token: authToken,
        },
      );
      if (response && response.job_status === "Completed") {
        setIsGenerating(false);
        setTimeout(() => {
          navigate(
            `/dashboard/feedback/${insightId}?conversation_insight_complete`,
          );
        }, 200);
      } else {
        setTranscriptData((prev) => ({
          ...prev,
          generationProgress: {
            job_status: response.job_status,
            job_status_steps: response.job_status_steps,
          },
        }));
        setTimeout(() => pollFeedbackStatus(insightId), 1000);
      }
    } catch (err) {
      setIsGenerating(false);
    }
  };

  const startFeedbackGeneration = async () => {
    setModal({ type: "", show: false, title: "" });
    setIsGenerating(true);

    const formData = new FormData();
    formData.append("transcript", transcriptData.file);
    formData.append("name", transcriptData.name);
    formData.append("subject", transcriptData.subject);
    if (transcriptData.training_program_id) {
      formData.append("trainingprogram_id", transcriptData.training_program_id);
    }

    await callAPI("conversation_insight/start", {
      method: "POST",
      body: formData,
      token: authToken,
    })
      .then((data) => {
        const newInsightId = data.conversation_insight_id;
        if (newInsightId) {
          setTranscriptData((prev) => ({ ...prev, id: newInsightId }));
          pollFeedbackStatus(newInsightId);
        }
      })
      .catch(() => {
        setIsGenerating(false);
      });
  };

  return (
    <div className="dashboard-special-widget ci">
      {generating && (
        <StackedProgress apiProgress={transcriptData.generationProgress} />
      )}
      <div className="widget-title">
        <div className="heading">
          <div className="icon">
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <g clip-path="url(#clip0_173_68)">
                <path
                  d="M6.66658 9.99998L4.61641 12.0758C4.33044 12.3653 4.18746 12.5101 4.06455 12.5203C3.95793 12.5292 3.85353 12.4863 3.78389 12.4051C3.70362 12.3114 3.70362 12.108 3.70362 11.701V10.6611C3.70362 10.296 3.40463 10.0318 3.04339 9.97886C2.16908 9.8508 1.48244 9.16415 1.35437 8.28984C1.33325 8.14568 1.33325 7.97366 1.33325 7.62961V4.53331C1.33325 3.41321 1.33325 2.85316 1.55124 2.42533C1.74299 2.04901 2.04895 1.74305 2.42527 1.5513C2.85309 1.33331 3.41315 1.33331 4.53325 1.33331H9.46659C10.5867 1.33331 11.1467 1.33331 11.5746 1.5513C11.9509 1.74305 12.2569 2.04901 12.4486 2.42533C12.6666 2.85316 12.6666 3.41321 12.6666 4.53331V7.33331M12.6666 14.6666L11.2156 13.6579C11.0117 13.5161 10.9097 13.4452 10.7987 13.3949C10.7002 13.3503 10.5966 13.3179 10.4903 13.2983C10.3705 13.2762 10.2463 13.2762 9.99786 13.2762H8.79992C8.05318 13.2762 7.67981 13.2762 7.3946 13.1308C7.14371 13.003 6.93974 12.799 6.81191 12.5482C6.66659 12.2629 6.66658 11.8896 6.66658 11.1428V9.46665C6.66658 8.71991 6.66659 8.34654 6.81191 8.06133C6.93974 7.81044 7.14371 7.60647 7.3946 7.47864C7.67981 7.33331 8.05318 7.33331 8.79992 7.33331H12.5333C13.28 7.33331 13.6534 7.33331 13.9386 7.47864C14.1895 7.60647 14.3934 7.81044 14.5213 8.06133C14.6666 8.34654 14.6666 8.71991 14.6666 9.46665V11.2762C14.6666 11.8974 14.6666 12.2081 14.5651 12.4531C14.4298 12.7798 14.1702 13.0394 13.8435 13.1747C13.5985 13.2762 13.2878 13.2762 12.6666 13.2762V14.6666Z"
                  stroke="#962EFF"
                  stroke-width="1.2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </g>
              <defs>
                <clipPath id="clip0_173_68">
                  <rect width="16" height="16" fill="white" />
                </clipPath>
              </defs>
            </svg>
          </div>
          <h2>Conversation Insights</h2>
        </div>
        <span className="badge-new">
          <svg
            width="12"
            height="12"
            viewBox="0 0 12 12"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <g clip-path="url(#clip0_173_10)">
              <path
                d="M2.25 11V8.5M2.25 3.5V1M1 2.25H3.5M1 9.75H3.5M6.5 1.5L5.63291 3.75443C5.49191 4.12105 5.4214 4.30435 5.31177 4.45854C5.21459 4.5952 5.0952 4.71459 4.95854 4.81176C4.80435 4.9214 4.62105 4.99191 4.25443 5.13291L2 6L4.25443 6.86709C4.62105 7.00809 4.80435 7.0786 4.95854 7.18823C5.0952 7.28541 5.21459 7.4048 5.31177 7.54146C5.4214 7.69565 5.49191 7.87895 5.63291 8.24557L6.5 10.5L7.36709 8.24557C7.5081 7.87895 7.5786 7.69565 7.68824 7.54146C7.78541 7.4048 7.9048 7.28541 8.04146 7.18823C8.19565 7.0786 8.37895 7.00809 8.74557 6.86709L11 6L8.74557 5.13291C8.37895 4.99191 8.19565 4.9214 8.04146 4.81176C7.9048 4.71459 7.78541 4.5952 7.68824 4.45854C7.5786 4.30435 7.50809 4.12105 7.36709 3.75443L6.5 1.5Z"
                stroke="#030039"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </g>
            <defs>
              <clipPath id="clip0_173_10">
                <rect width="12" height="12" fill="white" />
              </clipPath>
            </defs>
          </svg>
          New
        </span>
      </div>
      <p className="widget-description">
        Upload a meeting transcript and get instant feedback on communication,
        engagement, and areas to improve.
      </p>
      <div className={`form-container ${isLoading ? "uploading" : ""}`}>
                      {isLoading && (
                <div className="uploading-loading">
                  <CircularProgress enableTrackSlot />
                  <span>Validating transcript...</span>
                </div>
              )}
        <Form action="#">
          {({
            register,
            formState: { errors },
            watch,
            control,
            setValue,
            getValues,
          }) => (
            <>
              <TitleWatcher
                watch={watch}
                setTranscriptData={setTranscriptData}
              />
              <ProgramWatcher
                setValue={setValue}
                options={trainingProgramOptions}
                setTranscriptData={setTranscriptData}
                currentProgramId={transcriptData.training_program_id}
              />

              <div className="inline-fields">
                <Input
                  id="program_filter"
                  label="Category"
                  showLabel
                  type="select"
                  placeHolder="Select Category"
                  register={register("program_filter")}
                  control={control}
                  options={trainingProgramOptions}
                  onOptionSelect={(option) =>
                    setTranscriptData((prev) => ({
                      ...prev,
                      training_program_id: option,
                    }))
                  }
                  error={errors.program_filter?.message}
                  dark
                />
                <Input
                  type="text"
                  id="conversation_insight_name"
                  register={register("conversation_insight_name")}
                  label="Title"
                  showLabel
                  placeHolder="Enter a title for your transcript review"
                  dark
                />
              </div>

              <DragDropFileUpload
                showProgress
                onFileRemove={() => {
                  setTranscriptData((prev) => ({
                    ...prev,
                    file: "",
                    name: "",
                    availableSubjects: [],
                  }));
                  setIsValidFile(false);
                  setValue("conversation_insight_name", "", {
                    shouldValidate: true,
                    shouldDirty: true,
                  });
                }}
                onFilesSelected={(
                  files,
                  setUploadProgress,
                  setError,
                  setSuccess,
                ) => {
                  const currentTitle = getValues("conversation_insight_name");
                  if (
                    (!currentTitle || currentTitle.trim() === "") &&
                    files.length > 0
                  ) {
                    setValue(
                      "conversation_insight_name",
                      files[0].name.replace(/\.[^/.]+$/, ""),
                      { shouldValidate: true, shouldDirty: true },
                    );
                  }
                  handleFilesSelected(
                    files,
                    setUploadProgress,
                    setError,
                    setSuccess,
                  );
                }}
                maxFiles={1}
                accept="application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain"
                maxSize={50 * 1024 * 1024}
                uploadTypeText="TXT, DOCX, DOC, JSON, HTML, CSV (2MB MAX)"
              />

              <div className="actions">
                <DefaultButton
                  onClick={() =>
                    setModal({
                      type: "speaker_select",
                      show: true,
                      title: "Identify yourself",
                    })
                  }
                  wideOnMobile
                  buttonSize="lg"
                  disabled={!isValidFile}
                >
                  Generate Feedback
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
  <path d="M4.16675 9.99996H15.8334M15.8334 9.99996L10.0001 4.16663M15.8334 9.99996L10.0001 15.8333" stroke="#FEFEFE" stroke-width="1.66667" stroke-linecap="round" stroke-linejoin="round"/>
</svg>
                </DefaultButton>
              </div>
            </>
          )}
        </Form>
      </div>

      <Modal
        id="ci-widget-modal"
        title={modal.title}
        open={modal.show}
        onClose={() => setModal({ type: "", show: false, title: "" })}
        showIcon
        showClose={false}
        icon={<PeopleOutlineIcon />}
        slim
      >
        {modal.type === "speaker_select" && (
          <>
            <p>
              We detected {transcriptData.availableSubjects.length} speakers in
              the transcript, please confirm which one you are.
            </p>
            <div className="available-subjects">
              {transcriptData.availableSubjects.map((speaker, i) => (
                <button
                  key={i}
                  className={`speaker ${speaker === transcriptData.subject ? "active" : ""}`}
                  onClick={() =>
                    setTranscriptData((prev) => ({ ...prev, subject: speaker }))
                  }
                >
                  <PersonOutlineIcon
                    style={{
                      color:
                        speaker === transcriptData.subject ? "#962EFF" : "",
                    }}
                  />{" "}
                  {speaker}
                </button>
              ))}
            </div>
            <div className="actions">
              {transcriptData.subject && (
                <DefaultButton
                  onClick={() => startFeedbackGeneration()}
                  wideOnMobile
                  buttonSize="lg"
                  className="action-button"
                >
                  Start Generating
                </DefaultButton>
              )}
              <button
                className="cancel"
                onClick={() => setModal({ type: "", show: false, title: "" })}
              >
                Cancel
              </button>
            </div>
          </>
        )}
      </Modal>
    </div>
  );
};

export default CIWidget;
